export const getSafeString = (value: unknown, fallback: string = ''): string => {
    return typeof value === 'string' ? value : fallback;
};

export const resolveDataSourceName = (
    sourceName: unknown,
    destinationName: unknown
): string => {
    return (
        getSafeString(sourceName) ||
        getSafeString(destinationName) ||
        'target_data_source'
    );
};

export const resolveGlueTableName = (value: unknown): string => {
    return getSafeString(value);
};

export const getTargetTableType = (
    category: string
): 'file' | 'collection' | 'table' => {
    if (category === 'filesystem') return 'file';
    if (category === 'nosql') return 'collection';
    return 'table';
};

/** Same shape as `RegisteredDestinationConnectionDetails` (child import stays optional). */
export type RegisteredListConnectionDetails = {
    host: string
    port: string
    database: string
    username: string
    password: string
    schema: string
}

function normalizeInstanceProbe(value: string): string {
    return value.trim().toLowerCase().replaceAll(/[\s_-]+/g, '');
}

/**
 * Matches Angular behavior where only schema-aware engines should populate Schema Name.
 * Databases like MySQL use only `database` and should keep schema empty.
 */
export function supportsSchemaForInstance(instance: string | undefined | null): boolean {
    const probe = normalizeInstanceProbe(instance ?? '');
    if (!probe) return true;

    const schemaAwareInstances = new Set([
        'postgres',
        'postgresql',
        'redshift',
        'mssql',
        'sqlserver',
        'snowflake',
        'oracle',
        'db2',
    ]);
    return schemaAwareInstances.has(probe);
}

/** Angular parity: `postgres` -> `Postgres`, `MYSQL` -> `Mysql`. */
export function toDataSourceInstanceLabel(instance: string | undefined | null): string {
    const raw = instance?.trim() ?? '';
    if (!raw) return '';
    return raw.substring(0, 1).toUpperCase() + raw.substring(1).toLowerCase();
}

/** Angular parity: lowercase and strip spaces (`NO SQL` -> `nosql`). */
export function toDataSourceTypeFormatted(type: string | undefined | null): string {
    return (type?.toLowerCase() ?? '').replaceAll(' ', '');
}

function isInvalidEndpointQueryToken(query: string): boolean {
    const t = query.trim().toLowerCase();
    return !t || t === 'undefined' || t === 'null' || t === 'nan';
}

/** Picks the first non-empty string; treats `undefined` / `null` literals from bad APIs as empty. */
export function firstUsableConnectionString(...values: Array<string | undefined | null>): string {
    for (const v of values) {
        const t = v?.trim() ?? '';
        if (!t) continue;
        if (isInvalidEndpointQueryToken(t)) continue;
        return t;
    }
    return '';
}

export function firstNonEmptyTrimmed(...values: Array<string | undefined | null>): string {
    for (const v of values) {
        const t = v?.trim() ?? '';
        if (t) return t;
    }
    return '';
}

export function extractSchemaNameFromEndpoint(endpoint: string): string {
    const trimmed = endpoint.trim();
    if (!trimmed) return '';
    const query = (trimmed.split('?')[1] ?? '').trim();
    if (isInvalidEndpointQueryToken(query)) return '';
    return query;
}

export function extractHostPortFromEndpoint(endpoint: string): { host: string; port: string } {
    const trimmed = endpoint.trim();
    if (!trimmed) return { host: '', port: '' };
    const basePart = trimmed.split('?')[0]?.trim() ?? '';
    if (!basePart) return { host: '', port: '' };

    const candidate = basePart.includes('://') ? basePart : `proto://${basePart}`;
    try {
        const url = new URL(candidate);
        return {
            host: url.hostname ?? '',
            port: url.port ?? '',
        };
    } catch {
        const withoutProto = basePart.replace(/^[a-z]+:\/\//i, '');
        const hostAndPort = withoutProto.split('/')[0] ?? '';
        const [host, port] = hostAndPort.split(':');
        return {
            host: host?.trim() ?? '',
            port: port?.trim() ?? '',
        };
    }
}

/** Strip `?...` garbage sometimes appended to `port` fields from APIs. */
export function normalizePortString(raw: string | undefined | null): string {
    if (raw == null) return '';
    return raw.split('?')[0]?.trim() ?? '';
}

/**
 * Produces a numeric port when the string is a plain integer, otherwise returns the cleaned string
 * (for exotic cases). `undefined` when empty after normalization.
 */
export function normalizePortForDatabasePayload(
    raw: string | number | undefined | null
): number | string | undefined {
    if (raw === undefined || raw === null) return undefined;
    const s = typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : normalizePortString(String(raw));
    if (!s) return undefined;
    const n = Number(s);
    return Number.isFinite(n) && String(n) === s ? n : s;
}

/**
 * Build connection fields from a registered data source row (list API) so child components
 * and payloads can work before/without `getRegisteredTables` `databases[0]`.
 */
export function buildConnectionDetailsFromRegisteredListItem(args: {
    databaseName: string
    username: string
    password: string
    endpoint: string
}): RegisteredListConnectionDetails {
    const { host, port: hostPort } = extractHostPortFromEndpoint(args.endpoint);
    return {
        host: host,
        port: normalizePortString(hostPort),
        database: args.databaseName?.trim() ?? '',
        username: args.username?.trim() ?? '',
        password: args.password?.trim() ?? '',
        schema: extractSchemaNameFromEndpoint(args.endpoint),
    };
}